import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format currency in BRL
export function formatCurrency(value) {
  if (value === null || value === undefined) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

// Format date in Brazilian format
export function formatDate(date, options = {}) {
  if (!date) return '-';
  // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
  let d;
  if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = date.split('-');
    d = new Date(year, month - 1, day);
  } else {
    d = new Date(date);
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options,
  }).format(d);
}

// Format date for input (returns YYYY-MM-DD without timezone conversion)
export function formatDateInput(date) {
  if (!date) return '';
  // If already in YYYY-MM-DD format, return as is
  if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return date;
  }
  // If it has time component, take only the date part
  if (typeof date === 'string' && date.includes('T')) {
    return date.split('T')[0];
  }
  // For Date objects
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Parse Brazilian date string to Date
export function parseBRDate(dateStr) {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split('/');
  return new Date(year, month - 1, day);
}

// Format percentage
export function formatPercent(value, decimals = 1) {
  if (value === null || value === undefined) return '0%';
  return `${(value * 100).toFixed(decimals)}%`;
}

// Format number with Brazilian locale
export function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined) return '0';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

// Get status color
export function getStatusColor(status) {
  const colors = {
    pending: 'warning',
    paid: 'success',
    received: 'success',
    overdue: 'destructive',
    cancelled: 'muted',
    partial: 'accent',
  };
  return colors[status] || 'muted';
}

// Get status label in Portuguese
export function getStatusLabel(status) {
  const labels = {
    pending: 'Pendente',
    paid: 'Pago',
    received: 'Recebido',
    overdue: 'Vencido',
    cancelled: 'Cancelado',
    partial: 'Parcial',
    active: 'Ativo',
    inactive: 'Inativo',
  };
  return labels[status] || status;
}

// Get transaction type label
export function getTransactionTypeLabel(type) {
  const labels = {
    income: 'Receita',
    expense: 'Despesa',
    transfer: 'Transferência',
  };
  return labels[type] || type;
}

// Get account type label
export function getAccountTypeLabel(type) {
  const labels = {
    checking: 'Conta Corrente',
    savings: 'Poupança',
    cash: 'Caixa',
    credit_card: 'Cartão de Crédito',
    investment: 'Investimento',
  };
  return labels[type] || type;
}

// Get person type label
export function getPersonTypeLabel(type) {
  const labels = {
    customer: 'Cliente',
    supplier: 'Fornecedor',
    both: 'Cliente/Fornecedor',
  };
  return labels[type] || type;
}

// Calculate days until due
export function daysUntilDue(dueDate) {
  if (!dueDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  return diff;
}

// Get due date status class
export function getDueDateClass(dueDate, status) {
  if (status === 'paid' || status === 'received' || status === 'cancelled') {
    return 'text-muted-foreground';
  }
  const days = daysUntilDue(dueDate);
  if (days === null) return '';
  if (days < 0) return 'text-destructive font-medium';
  if (days <= 3) return 'text-warning font-medium';
  if (days <= 7) return 'text-accent font-medium';
  return '';
}

// Generate unique ID
export function generateId() {
  return crypto.randomUUID();
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Format CPF/CNPJ
export function formatDocument(doc) {
  if (!doc) return '-';
  const cleaned = doc.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (cleaned.length === 14) {
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return doc;
}

// Format phone
export function formatPhone(phone) {
  if (!phone) return '-';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  return phone;
}

// Download blob as file
export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// Get initials from name
export function getInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(' ');
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

// Truncate text
export function truncate(text, length = 50) {
  if (!text) return '';
  if (text.length <= length) return text;
  return text.substring(0, length) + '...';
}
